import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Select, Input, Button, Space,
  Drawer, Typography, Popconfirm, Alert, Badge, message, Tooltip
} from 'antd';
import {
  BugOutlined, ReloadOutlined, CheckCircleOutlined, DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { io } from 'socket.io-client';
import Cookies from 'js-cookie';
import moment from 'moment';
import { adminAPI } from '../api/admin';

const { Text, Paragraph } = Typography;
const { Search } = Input;

const SOURCE_LABELS = {
  main: { label: 'بک‌اند اصلی', color: 'blue' },
  chat: { label: 'چت هوش مصنوعی', color: 'purple' },
  'diagon-alley': { label: 'کوچه دیاگون', color: 'gold' },
  'android-app': { label: 'اپلیکیشن اندروید', color: 'red' }
};

const SEVERITY_COLORS = {
  critical: 'red',
  error: 'volcano',
  warning: 'gold'
};

// آدرس Socket.IO از همان REACT_APP_API_URL مشتق می‌شود (با حذف پسوند /api) —
// در production که فرانت/بک‌اند هم‌مبدأ هستند مقدارش خالی می‌ماند و
// socket.io-client خودش به همان origin وصل می‌شود؛ در dev اگر REACT_APP_API_URL
// روی آدرس بک‌اند تنظیم شده باشد همان استفاده می‌شود.
function getSocketBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  return apiUrl.replace(/\/api\/?$/, '');
}

export default function ErrorMonitor() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [diagonAlleyReachable, setDiagonAlleyReachable] = useState(true);
  const [liveConnected, setLiveConnected] = useState(false);
  const [filters, setFilters] = useState({ source: undefined, resolved: undefined, severity: undefined, q: '' });
  const [selected, setSelected] = useState(null);

  const socketRef = useRef(null);

  const fetchList = useCallback(async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { page: targetPage, limit };
      if (filters.source) params.source = filters.source;
      if (filters.resolved !== undefined) params.resolved = filters.resolved;
      if (filters.severity) params.severity = filters.severity;
      if (filters.q) params.q = filters.q;
      const { data } = await adminAPI.getErrors(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setDiagonAlleyReachable(data.diagonAlleyReachable !== false);
    } catch (e) {
      message.error('خطا در دریافت لیست خطاها');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await adminAPI.getErrorStats();
      setStats(data.stats);
      if (data.diagonAlleyReachable === false) setDiagonAlleyReachable(false);
    } catch (e) {
      // بی‌سروصدا — کارت‌های آماری صرفاً خالی می‌مانند
    }
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ─── اتصال زنده (Socket.IO) ───────────────────────────────────────────────
  useEffect(() => {
    const token = Cookies.get('token');
    if (!token) return undefined;

    const socket = io(`${getSocketBaseUrl()}/admin-errors`, {
      auth: { token },
      reconnection: true,
      transports: ['websocket', 'polling']
    });
    socketRef.current = socket;

    socket.on('connect', () => setLiveConnected(true));
    socket.on('disconnect', () => setLiveConnected(false));
    socket.on('connect_error', () => setLiveConnected(false));

    socket.on('new-error', (doc) => {
      message.warning({
        content: `خطای جدید (${SOURCE_LABELS[doc.source]?.label || doc.source}): ${doc.message?.slice(0, 80) || ''}`,
        duration: 6
      });
      setStats((prev) => {
        if (!prev) return prev;
        const src = doc.source === 'diagon-alley' ? 'diagon-alley' : (doc.context === 'chat' ? 'chat' : (doc.source === 'android-app' ? 'android-app' : 'main'));
        const bySource = { ...prev.bySource };
        if (bySource[src]) {
          bySource[src] = {
            ...bySource[src],
            total: (bySource[src].total || 0) + 1,
            unresolved: (bySource[src].unresolved || 0) + 1,
            last24h: (bySource[src].last24h || 0) + 1
          };
        }
        return {
          ...prev,
          total: (prev.total || 0) + 1,
          unresolved: (prev.unresolved || 0) + 1,
          last24h: (prev.last24h || 0) + 1,
          bySource
        };
      });
      // فقط اگر روی صفحه‌ی اول و بدون فیلتر متن هستیم، خودمان ردیف را اضافه کن
      setPage((currentPage) => {
        if (currentPage === 1) {
          setItems((prev) => [doc, ...prev].slice(0, limit));
          setTotal((prev) => prev + 1);
        }
        return currentPage;
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResolve = async (record) => {
    try {
      await adminAPI.resolveError(record._id, record.source === 'diagon-alley' ? 'diagon-alley' : (record.context === 'chat' ? 'chat' : (record.source === 'android-app' ? 'android-app' : 'main')));
      message.success('علامت‌گذاری شد');
      fetchList(page);
      fetchStats();
    } catch (e) {
      message.error('عملیات ناموفق بود');
    }
  };

  const handleDelete = async (record) => {
    try {
      await adminAPI.deleteError(record._id, record.source === 'diagon-alley' ? 'diagon-alley' : (record.context === 'chat' ? 'chat' : (record.source === 'android-app' ? 'android-app' : 'main')));
      message.success('حذف شد');
      fetchList(page);
      fetchStats();
    } catch (e) {
      message.error('حذف ناموفق بود');
    }
  };

  const columns = [
    {
      title: 'زمان',
      dataIndex: 'createdAt',
      width: 140,
      render: (v) => (
        <Tooltip title={moment(v).format('YYYY-MM-DD HH:mm:ss')}>
          <Text type="secondary" style={{ fontSize: 12 }}>{moment(v).fromNow()}</Text>
        </Tooltip>
      )
    },
    {
      title: 'منبع',
      dataIndex: 'source',
      width: 130,
      render: (source, record) => {
        const key = record.context === 'chat' ? 'chat' : source;
        const meta = SOURCE_LABELS[key] || { label: key, color: 'default' };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      }
    },
    {
      title: 'شدت',
      dataIndex: 'severity',
      width: 90,
      render: (s) => <Tag color={SEVERITY_COLORS[s] || 'default'}>{s || 'error'}</Tag>
    },
    {
      title: 'مسیر / زمینه',
      dataIndex: 'path',
      render: (_, record) => (
        <Text style={{ fontSize: 12 }} type="secondary">
          {record.method ? `${record.method} ` : ''}{record.path || record.context}
        </Text>
      )
    },
    {
      title: 'پیام',
      dataIndex: 'message',
      render: (msg, record) => (
        <a onClick={() => setSelected(record)} style={{ color: '#722f37' }}>
          {msg?.length > 90 ? `${msg.slice(0, 90)}…` : msg}
        </a>
      )
    },
    {
      title: 'وضعیت',
      dataIndex: 'resolved',
      width: 110,
      render: (resolved) => resolved
        ? <Tag icon={<CheckCircleOutlined />} color="success">حل‌شده</Tag>
        : <Tag icon={<ExclamationCircleOutlined />} color="error">باز</Tag>
    },
    {
      title: 'عملیات',
      width: 140,
      render: (_, record) => (
        <Space>
          {!record.resolved && (
            <Button size="small" onClick={() => handleResolve(record)}>حل شد</Button>
          )}
          <Popconfirm title="حذف این رکورد؟" onConfirm={() => handleDelete(record)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      {!diagonAlleyReachable && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="اتصال به کوچه دیاگون برقرار نیست"
          description="خطاهای بک‌اند اصلی و چت نمایش داده می‌شوند، اما لیست/آمار کوچه دیاگون قابل دریافت نبود. اگر آن اپ پایین است یا JWT_SECRET یکسان نیست را چک کنید."
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8} md={6}>
          <Card size="small">
            <Statistic title="کل خطاها" value={stats?.total ?? '—'} prefix={<BugOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card size="small">
            <Statistic title="حل‌نشده" value={stats?.unresolved ?? '—'} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card size="small">
            <Statistic title="۲۴ ساعت گذشته" value={stats?.last24h ?? '—'} valueStyle={{ color: '#c07f11' }} />
          </Card>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Card size="small">
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 13 }}>وضعیت لحظه‌ای</Text>
              <Badge status={liveConnected ? 'success' : 'default'} text={liveConnected ? 'متصل — دریافت زنده' : 'در حال اتصال...'} />
            </Space>
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title="فیلترها"
        extra={<Button icon={<ReloadOutlined />} onClick={() => { fetchList(page); fetchStats(); }}>بروزرسانی</Button>}
      >
        <Space wrap>
          <Select
            allowClear
            placeholder="منبع"
            style={{ width: 160 }}
            value={filters.source}
            onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, source: v })); }}
            options={[
              { value: 'main', label: 'بک‌اند اصلی' },
              { value: 'chat', label: 'چت هوش مصنوعی' },
              { value: 'diagon-alley', label: 'کوچه دیاگون' },
              { value: 'android-app', label: 'اپلیکیشن اندروید' }
            ]}
          />
          <Select
            allowClear
            placeholder="وضعیت"
            style={{ width: 140 }}
            value={filters.resolved}
            onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, resolved: v })); }}
            options={[
              { value: 'false', label: 'فقط باز' },
              { value: 'true', label: 'فقط حل‌شده' }
            ]}
          />
          <Select
            allowClear
            placeholder="شدت"
            style={{ width: 140 }}
            value={filters.severity}
            onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, severity: v })); }}
            options={[
              { value: 'critical', label: 'critical' },
              { value: 'error', label: 'error' },
              { value: 'warning', label: 'warning' }
            ]}
          />
          <Search
            placeholder="جستجو در پیام خطا..."
            style={{ width: 260 }}
            allowClear
            onSearch={(v) => { setPage(1); setFilters((f) => ({ ...f, q: v })); }}
          />
        </Space>
      </Card>

      <Card size="small">
        <Table
          rowKey={(r) => `${r.source}-${r.context}-${r._id}`}
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: false,
            onChange: (p) => setPage(p)
          }}
        />
      </Card>

      <Drawer
        title="جزئیات خطا"
        open={!!selected}
        onClose={() => setSelected(null)}
        width={640}
      >
        {selected && (
          <>
            <Paragraph>
              <Text strong>پیام: </Text>{selected.message}
            </Paragraph>
            <Paragraph>
              <Text strong>زمان: </Text>{moment(selected.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Paragraph>
            {selected.path && (
              <Paragraph>
                <Text strong>مسیر: </Text>{selected.method} {selected.path}
              </Paragraph>
            )}
            {selected.statusCode && (
              <Paragraph>
                <Text strong>کد وضعیت: </Text>{selected.statusCode}
              </Paragraph>
            )}
            {selected.meta && Object.keys(selected.meta).length > 0 && (
              <Paragraph>
                <Text strong>اطلاعات جانبی (Meta):</Text>
                <pre style={{
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 6,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 200,
                  overflow: 'auto'
                }}>
                  {JSON.stringify(selected.meta, null, 2)}
                </pre>
              </Paragraph>
            )}
            <Paragraph>
              <Text strong>Stack Trace:</Text>
              <pre style={{
                background: '#f5f5f5',
                padding: 12,
                borderRadius: 6,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 400,
                overflow: 'auto'
              }}>
                {selected.stack || 'موجود نیست'}
              </pre>
            </Paragraph>
          </>
        )}
      </Drawer>
    </div>
  );
}
